import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, Phone, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import BookingModal from '@/components/BookingModal';
import JsonLd, { generateServiceSchema, generateBreadcrumbSchema } from '@/components/seo/JsonLd';
import FAQSection from '@/components/FAQSection';
import SEOHead from '@/components/seo/SEOHead';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import OptimizedImage from '@/components/ui/OptimizedImage';
import watchImage from '@/assets/watch-repair.jpg';

const watchServiceSchema = generateServiceSchema({
  name: 'Apple Watch Repair Service in Patna, Bihar',
  description: 'Expert Apple Watch repair services in Bihar. Screen replacement, battery replacement, sensor repair for all Apple Watch models from Series 3 to Ultra 2.',
  url: 'https://ifixappleservicecenter.in/watch-service',
  ratingValue: 4.9,
  reviewCount: 180,
});

const watchBreadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://ifixappleservicecenter.in/' },
  { name: 'Apple Watch Service', url: 'https://ifixappleservicecenter.in/watch-service' },
]);

const watchFaqs = [
  {
    question: 'Can you replace Apple Watch screen in Patna?',
    answer: 'Yes, we specialize in Apple Watch screen replacement for all models from Series 3 to Ultra 2. Most screen replacements are completed within 24-48 hours due to the precision work required.',
  },
  {
    question: 'How much does Apple Watch battery replacement cost?',
    answer: 'Apple Watch battery replacement costs vary by model and series. Contact us at +91 97986 53591 for an accurate quote. Battery health is diagnosed before replacement to ensure it resolves your issue.',
  },
  {
    question: 'Can you fix water-damaged Apple Watch?',
    answer: 'While Apple Watch is water-resistant, damage can occur from deep water exposure or seal degradation. We offer water damage assessment and repair services with honest success rate estimates.',
  },
  {
    question: 'Do you repair Apple Watch heart rate sensor issues?',
    answer: 'Yes, we diagnose and repair heart rate sensor and back crystal issues. This may involve sensor cleaning, recalibration, or replacement depending on the underlying cause.',
  },
  {
    question: 'Is same-day Apple Watch repair available?',
    answer: 'Due to the precision nature of Apple Watch repairs, most services take 24-48 hours. However, simple diagnostics and battery assessments can be done same-day.',
  },
];



const WatchService = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const services = [
    'Screen Replacement',
    'Battery Replacement',
    'Logic Board Repair',
    'Back Sensor Replacement',
    'Water Damage Repair',
    'Overheating Issue',
    'Network Issue',
    'Stuck on Logo',
  ];

  const models = [
    'Apple Watch Series 3',
    'Apple Watch Series 4',
    'Apple Watch Series 5',
    'Apple Watch SE (1st Gen)',
    'Apple Watch Series 6',
    'Apple Watch Series 7',
    'Apple Watch SE (2nd Gen)',
    'Apple Watch Series 8',
    'Apple Watch Series 9',
    'Apple Watch Series 10',
    'Apple Watch Ultra',
    'Apple Watch Ultra 2',
  ];

  return (
    <Layout>
      <SEOHead
        title="Apple Watch Repair Patna Bihar | Apple Care Center Near Me - iFix"
        description="Expert Apple Watch repair in Bihar. Apple apple watch repair for screen, battery & sensor replacement for Series 3 to Ultra 2. Apple service near me with same-day service. Book your repair today!"
        url="https://ifixappleservicecenter.in/watch-service"
      />
      <JsonLd data={watchServiceSchema} />
      <JsonLd data={watchBreadcrumbSchema} />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src={watchImage}
            alt="Apple Watch repair service center in Patna Bihar - screen replacement battery sensor fix"
            className="w-full h-full object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
        </div>
        
        <div className="container-apple relative z-10 py-20">
          <PageBreadcrumb 
            items={[{ name: 'Apple Watch Service', path: '/watch-service' }]} 
            className="mb-8"
          />
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 opacity-0 animate-fade-in">
            Apple Watch Repair
            <span className="block text-primary">in Patna, Bihar</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl leading-relaxed opacity-0 animate-fade-in animation-delay-100">
            Looking for Apple Watch repair near me? iFix is your trusted Apple care center near me in Bihar offering precision Apple apple watch repair for screen, battery, 
            and sensor issues on all Apple Watch models. Visit our Apple service near me for expert service.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in animation-delay-200">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Watch Repair
            </Button>
            <Button variant="whatsapp" size="lg" asChild>
              <a
                href="https://wa.me/919798653591?text=Hi,%20I%20need%20help%20with%20my%20Apple%20Watch"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Apple Watch Repair Services
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Expert Apple apple watch repair services at our Apple service center in Patna. Precision repairs for your Apple Watch by certified technicians at our Apple care center near me.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {services.map((service, index) => (
              <div
                key={service}
                className="flex items-center gap-3 p-4 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="font-medium text-foreground">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Models */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Supported Apple Watch Models
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            We service all Apple Watch models from Series 3 to Apple Watch Ultra.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            {models.map((model) => (
              <span
                key={model}
                className="px-4 py-2 bg-background rounded-full text-sm font-medium text-foreground border border-border"
              >
                {model}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-8 text-center">
            Related Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link
              to="/blog/apple-watch-repair-bihar"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                Apple Watch Repair Guide
              </h3>
              <p className="text-sm text-muted-foreground">
                Learn about our Watch repair services in Bihar.
              </p>
            </Link>
            <Link
              to="/iphone-service"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                iPhone Repair Services
              </h3>
              <p className="text-sm text-muted-foreground">
                Pair your Watch repair with iPhone service.
              </p>
            </Link>
            <Link
              to="/ipad-service"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                iPad Repair Services
              </h3>
              <p className="text-sm text-muted-foreground">
                Professional iPad screen and charging repair.
              </p>
            </Link>
          </div>
          
          {/* Cross-linking */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">Explore more Apple repair services:</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/iphone-service" className="text-primary hover:underline font-medium">
                iPhone Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/macbook-service" className="text-primary hover:underline font-medium">
                MacBook Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/ipad-service" className="text-primary hover:underline font-medium">
                iPad Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/blog" className="text-primary hover:underline font-medium">
                All Repair Guides
              </Link>
              <span className="text-border">•</span>
              <Link to="/franchise" className="text-primary hover:underline font-medium">
                Franchise
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="Apple Watch Repair FAQs"
        subtitle="Common questions about our Apple Watch repair services"
        faqs={watchFaqs}
      />

      {/* CTA Section */}
      <section className="section-padding bg-foreground">
        <div className="container-apple text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-background mb-4">
            Need Apple Watch Repair?
          </h2>
          <p className="text-background/70 mb-8 max-w-xl mx-auto">
            Get your Apple Watch repaired by specialists. Screen and battery replacement available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Appointment
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </Layout>
  );
};

export default WatchService;
